import { Bauldron, ItemTypes, Key } from "game-common/item/item_type"

export const HumanoidRaceTypes = ["human", "reptile", "reptile-winged", "orc", "skeleton", "wolfman", "zombie"] as const

export const RaceTypes = [
    ...HumanoidRaceTypes,
    "horse",
    "snake",
    "deer",
    "slime",
    "rabbit",
    "chicken",
    "bee",
    "wasp",
    "redspider",
    "spikes1",
] as const

export type Race = (typeof RaceTypes)[number]

export const SexTypes = ["male", "female"] as const

export type Sex = (typeof SexTypes)[number]

export const RaceSexes: Record<Race, Sex[]> = {
    human: ["male", "female"],
    reptile: ["male", "female"],
    "reptile-winged": ["male", "female"],
    orc: ["male", "female"],
    wolfman: ["male", "female"],
    zombie: ["male"],
    skeleton: ["male"],
    horse: ["male"],
    snake: ["male"],
    slime: ["male"],
    rabbit: ["male"],
    deer: ["female"],
    chicken: ["male"],
    bee: ["male"],
    wasp: ["male"],
    redspider: ["male"],
    spikes1: ["male"],
}

/**
 * The colour vocabulary, as an array so it exists at runtime.
 *
 * It used to be a bare union, which meant nothing could enumerate it: the sprite lab kept a
 * hand-typed copy for its colour picker, and the importer had no way to check that a colour it
 * was about to write was real. A `const` array gives the identical union plus a list, the same
 * shape `RaceTypes` and `HairTypes` already use.
 *
 * This is the vocabulary, not the art. Which colours a component comes in is generated from the
 * registry (see below); adding a NAME here is still a decision someone makes.
 */
export const ColorTypes = [
    "white",
    "blonde",
    "yellow",
    "light",
    "lightgreen",
    "gold",
    "green",
    "teal",
    "blue",
    "orange",
    "maroon",
    "red",
    "purple",
    "olive",
    "tan",
    "lightbrown",
    "brown",
    "dark",
    "gray",
    "silver",
    "metal",
    "pink",
    "black",
    "na",
] as const

export type Color = (typeof ColorTypes)[number]

import { SPRITE_COLORS } from "./sprite_colors.generated"

/**
 * The colour tables below are DERIVED from the sprite registry, not written here.
 *
 * Which colours a component comes in is a fact about the art on disk, and keeping it by hand is
 * what made importing a new colour a code change in three places. `sprite_colors.generated.ts`
 * is produced from `lpc/sprite_registry.json` by `yarn compile:lpc`; edit the registry (or use
 * the sprite lab) and regenerate.
 *
 * What stays hand-written, and why:
 *
 * - **`Color`** is the vocabulary. Naming a new colour is a decision, and the guard below means
 *   the data cannot invent one.
 * - **The `*Types` arrays.** Their order and grouping carry meaning -- `RaceTypes` puts the
 *   humanoids first because `HumanoidRaceTypes` is a prefix of it -- which a generator sorting
 *   keys would quietly destroy.
 * - **The `Sex*` tables.** Which sex is offered which garment is a design choice, not art.
 */

/** Widen one slot's readonly literal tuples to the `Color[]` every caller already expects. */
const derive = <T extends Record<string, readonly string[]>>(table: T): { [K in keyof T]: Color[] } =>
    Object.keys(table).reduce((out, key) => {
        out[key] = [...table[key]]
        return out
    }, {} as any)

export const RaceColors: Record<Race, Color[]> = derive(SPRITE_COLORS.body)

export const EyeTypes = ["normal", "cyclops"]

export type Eyes = (typeof EyeTypes)[number]

export const EyesColors: Record<Eyes, Color[]> = derive(SPRITE_COLORS.eyes)

export const NoseTypes = ["button", "big", "straight"] as const

export type Nose = (typeof NoseTypes)[number] | "none"

const commonNoseColor: Color[] = ["white", "tan", "gray"]

export const NoseColors: Record<Nose, Color[]> = {
    ...derive(SPRITE_COLORS.nose),
    // "none" is a Nose, not a component: it means the slot is empty, and the
    // forge returns no sheets for it. The art has nothing to say about it.
    none: [],
}

// todo
export const RaceNoses: Partial<Record<Race, Nose[]>> = {
    human: ["big", "button", "straight"],
    reptile: [],
    "reptile-winged": [],
    orc: ["big", "button", "straight"],
    skeleton: ["big", "button", "straight"],
    wolfman: [],
    zombie: ["big", "button", "straight"],
}

export const RaceEyes: Partial<Record<Race, Eyes[]>> = {
    human: ["normal"],
    orc: ["normal"],
    skeleton: ["normal"],
    zombie: ["normal"],
}

export const WeaponTypes = [
    "dagger",
    "spear",
    "bow",
    "crossbow",
    "staff",
    "axe",
    "warhammer",
    "longsword",
    "mace",
    "rapier",
    "saber",
    "scythe",
    "flail",
    "halberd",
    "waraxe",
    "web",
    "none",
] as const

export type Weapon = (typeof WeaponTypes)[number]

const commonWeapons: Weapon[] = ["spear", "bow", "staff", "axe", "warhammer", "none"]

export const WeaponAction: Record<Weapon, string> = {
    dagger: "slash",
    longsword: "slash",
    flail: "slash",
    halberd: "slash",
    waraxe: "slash",
    rapier: "slash",
    mace: "slash",
    saber: "slash",
    scythe: "slash",
    spear: "thrust",
    bow: "shoot",
    crossbow: "thrust",
    staff: "thrust",
    axe: "slash",
    warhammer: "slash",
    none: "slash",
    web: "shoot",
}

export const WeaponAmmo: Partial<Record<Weapon, ItemTypes>> = {
    bow: "arrow",
    crossbow: "arrow",
    web: "webAmmo",
}

export const WeaponColors: Record<Weapon, Color[]> = derive(SPRITE_COLORS.weapon)

export const RaceWeapons: Partial<Record<Race, Weapon[]>> = {
    human: [...commonWeapons],
    reptile: [...commonWeapons],
    "reptile-winged": [...commonWeapons],
    orc: [...commonWeapons],
    wolfman: [...commonWeapons],
    skeleton: [...commonWeapons],
    zombie: [...commonWeapons],
}

export const DefaultAttackActionType: Partial<Record<Race, ActionType>> = {
    snake: "thrust",
    slime: "thrust",
    wasp: "thrust",
    redspider: "thrust",
    skeleton: "thrust",
    zombie: "slash",
    spikes1: "thrust",
}

export const HairTypes = [
    "afro",
    "balding",
    "bangs",
    "bangs_bun",
    "bangslong",
    "bangslong2",
    "bangsshort",
    "bedhead",
    "bob",
    "bob_side_part",
    "braid",
    "braid2",
    "bunches",
    "buzzcut",
    "cornrows",
    "cowlick",
    "cowlick_tall",
    "curly_long",
    "curly_short",
    "curtains",
    "curtains_long",
    "dreadlocks_long",
    "dreadlocks_short",
    "flat_top_fade",
    "flat_top_straight",
    "half_up",
    "halfmessy",
    "high_and_tight",
    "high_ponytail",
    "idol",
    "jewfro",
    "lob",
    "long",
    "long_band",
    "long_center_part",
    "long_messy",
    "long_messy2",
    "long_tied",
    "longhawk",
    "longknot",
    "loose",
    "messy1",
    "messy2",
    "messy3",
    "mop",
    "natural",
    "page",
    "page2",
    "part2",
    "parted",
    // // "pigtails",
    // // "pigtails_bangs",
    "pixie",
    "plain",
    "ponytail",
    "ponytail2",
    "princess",
    "sara",
    "shorthawk",
    "shortknot",
    "shoulderl",
    "shoulderr",
    "single",
    "spiked",
    "spiked2",
    "spiked_beehive",
    "spiked_liberty",
    "spiked_liberty2",
    "spiked_porcupine",
    "swoop",
    "twists_fade",
    "twists_straight",
    "unkempt",
    "wavy",
    "xlong",
] as const

export type Hair = (typeof HairTypes)[number]

const commonHairColor: Color[] = [
    "white",
    "blonde",
    "gold",
    "green",
    "blue",
    "red",
    "orange",
    "purple",
    "brown",
    "lightbrown",
    "gray",
    "black",
]

export const HairColors: Record<Hair, Color[]> = derive(SPRITE_COLORS.hair)

export const SexHair: Record<Sex, Hair[]> = {
    male: [
        ...HairTypes.filter(
            hair =>
                ![
                    "bob",
                    "bob_side_part",
                    "braid",
                    "braid2",
                    "bunches",
                    "curly_long",
                    "long_band",
                    // "pigtails",
                    // "pigtails_bang",
                    "princess",
                    "sara",
                    "shoulderl",
                    "shoulderr",
                    "xlong",
                ].includes(hair),
        ),
    ],
    female: [...HairTypes.filter(hair => !["balding"].includes(hair))],
}

export const PantsTypes = ["pants-armor", "pants", "pantaloons", "legion", "skirts"] as const

export type Pants = (typeof PantsTypes)[number]

export const SexPants: Record<Sex, Pants[]> = {
    male: ["pants-armor", "pants", "pantaloons", "legion", "skirts"],
    female: ["pants-armor", "pants", "legion", "skirts"],
}

export const PantsColors: Record<Pants, Color[]> = derive(SPRITE_COLORS.pants)

export const ShirtTypes = [
    "blouse",
    "blouse_longsleeve",
    "corset",
    "longsleeve",
    "longsleeve_laced",
    // "robe",
    "scoop_shirt",
    "sleeveless",
    "sleeveless_laced",
    "sleeveless_striped",
    "tanktop",
    "tunic",
] as const

export type Shirt = (typeof ShirtTypes)[number]

export const SexShirts: Record<Sex, Shirt[]> = {
    male: ["longsleeve", "longsleeve_laced", "sleeveless", "sleeveless_laced", "sleeveless_striped"],
    female: [
        "blouse",
        "blouse_longsleeve",
        "corset",
        "longsleeve",
        // "robe",
        "scoop_shirt",
        "tanktop",
        "tunic",
    ],
}

const commonShirtColors: Color[] = ["black", "blue", "green", "maroon", "purple", "red", "teal", "white", "yellow"]

export const ShirtColors: Record<Shirt, Color[]> = derive(SPRITE_COLORS.shirt)

export const ArmorTypes = ["armor-plate", "armor-leather", "armor-legion"] as const

export type Armor = (typeof ArmorTypes)[number]

export const ArmorColors: Record<Armor, Color[]> = derive(SPRITE_COLORS.armor)

export const ShoeTypes = ["boots", "shoes", "shoes-armor", "slippers", "sandals"] as const

export type Shoe = (typeof ShoeTypes)[number]

export const SexShoes: Record<Sex, Shoe[]> = {
    male: ["shoes-armor", "boots", "sandals", "shoes"],

    female: ["shoes-armor", "boots", "sandals", "shoes", "slippers"],
}

export const ShoeColors: Record<Shoe, Color[]> = derive(SPRITE_COLORS.shoes)

export const ShieldTypes = [
    "shield-crusader",
    "shield-spartan",
    "shield-round",
    "shield-kite",
    "shield-kite_blue",
    "shield-kite_gray",
    "shield-kite_green",
    "shield-kite_red",
] as const

export type Shield = (typeof ShieldTypes)[number]

export const ShieldColors: Record<Shield, Color[]> = derive(SPRITE_COLORS.shield)

export const CapeTypes = ["cape-solid", "cape-tattered", "cape-trimmed"] as const

export type Cape = (typeof CapeTypes)[number]

export const SexCapes: Record<Sex, Cape[]> = {
    male: ["cape-solid"],
    female: ["cape-tattered", "cape-trimmed"],
}

export const CapeColors: Record<Cape, Color[]> = derive(SPRITE_COLORS.cape)

export const KeyColors: Record<Key, Color[]> = {
    castlekey: ["gold"],
}

export const BauldronColors: Record<Bauldron, Color[]> = derive(SPRITE_COLORS.bauldron)
export type ActionType =
    | "walk"
    | "idle"
    | "thrust"
    | "slash"
    | "shoot"
    | "hurt"
    | "activate"
    | "deactivate"
    | "active"
    | "inactive"


/**
 * Guards on the generated colour data. Both are compile-time only; neither runs.
 *
 * Without them "derived from data" would mean "unchecked", which is worse than the hand-written
 * tables it replaced.
 */

/**
 * 1. No value may be a colour the game does not have a name for. A registry that imported
 *    upstream's `chartreuse` fails the build here, naming it, rather than resolving to a path
 *    nothing renders.
 */
const _spriteColorsAreRealColors: Record<string, Record<string, readonly Color[]>> = SPRITE_COLORS
void _spriteColorsAreRealColors

/**
 * 2. Every declared component must have an entry. This is the exhaustiveness `Record<Race, ...>`
 *    used to give for free when the table was written out by hand: drop a race from the registry
 *    and the build fails instead of that race rendering nothing.
 */
const _everyComponentHasColours: [
    Record<Race, unknown>,
    Record<Hair, unknown>,
    Record<Weapon, unknown>,
    Record<Pants, unknown>,
    Record<Shirt, unknown>,
    Record<Armor, unknown>,
    Record<Shoe, unknown>,
    Record<Shield, unknown>,
    Record<Cape, unknown>,
    Record<Bauldron, unknown>,
] = [
    SPRITE_COLORS.body,
    SPRITE_COLORS.hair,
    SPRITE_COLORS.weapon,
    SPRITE_COLORS.pants,
    SPRITE_COLORS.shirt,
    SPRITE_COLORS.armor,
    SPRITE_COLORS.shoes,
    SPRITE_COLORS.shield,
    SPRITE_COLORS.cape,
    SPRITE_COLORS.bauldron,
]
void _everyComponentHasColours

/**
 * The literal-typed table, for code that wants the exact colours a component offers rather than
 * `Color[]`. `ColorsOf<"hair", "long">` is `"white" | "blonde" | ... | "black"`, checked at the
 * call site; the `Record<X, Color[]>` tables above stay as they are for every existing caller.
 */
export { SPRITE_COLORS }
export type ColorsOf<
    S extends keyof typeof SPRITE_COLORS,
    C extends keyof (typeof SPRITE_COLORS)[S],
> = (typeof SPRITE_COLORS)[S][C] extends readonly (infer T)[] ? T : never
